import { audioBufferToWav } from "./audioBufferToWav";

/**
 * Splice a recording blob into an AudioBuffer at the given region.
 * Uses crossfade (5ms) at splice boundaries to prevent clicks.
 *
 * All coordinates are in take-time (seconds on the take timeline).
 * The caller is responsible for any beat-time → take-time conversion.
 *
 * @param {AudioBuffer} originalBuffer - The original take audio
 * @param {Blob} recordingBlob - The new recording to splice in
 * @param {number} regionStart - Start time in seconds (take-time)
 * @param {number} regionEnd - End time in seconds (take-time)
 * @returns {Promise<Blob>} - WAV blob of the spliced result
 */
export const spliceAudio = async (
  originalBuffer,
  recordingBlob,
  regionStart,
  regionEnd,
) => {
  if (
    !Number.isFinite(regionStart) ||
    !Number.isFinite(regionEnd) ||
    regionStart >= regionEnd
  ) {
    throw new Error("Invalid region coordinates");
  }

  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();

  try {
    // Decode the punch recording
    const punchArrayBuffer = await recordingBlob.arrayBuffer();
    const punchBuffer = await audioCtx.decodeAudioData(punchArrayBuffer);

    const sampleRate = originalBuffer.sampleRate;
    const channels = originalBuffer.numberOfChannels;

    // Cap extension to prevent memory exhaustion (original + 60s max)
    const maxDuration = originalBuffer.duration + 60;
    if (regionEnd > maxDuration) {
      throw new Error("Region extends too far beyond existing audio");
    }

    // Extend the take buffer if the region goes beyond its current duration
    const neededDuration = Math.max(originalBuffer.duration, regionEnd);
    const totalSamples = Math.ceil(neededDuration * sampleRate);

    // If we need to extend, create a longer buffer padded with silence
    let workBuffer = originalBuffer;
    if (neededDuration > originalBuffer.duration + 0.01) {
      const extCtx = new OfflineAudioContext(
        channels,
        totalSamples,
        sampleRate,
      );
      const src = extCtx.createBufferSource();
      src.buffer = originalBuffer;
      src.connect(extCtx.destination);
      src.start(0);
      workBuffer = await extCtx.startRendering();
    }

    const regionStartSample = Math.floor(regionStart * sampleRate);
    const regionEndSample = Math.min(
      Math.floor(regionEnd * sampleRate),
      totalSamples,
    );

    // Crossfade duration (5ms) to prevent clicks at splice boundaries
    const crossfadeSamples = Math.floor(0.005 * sampleRate);

    // Create offline context for the final mix
    const offlineCtx = new OfflineAudioContext(
      channels,
      totalSamples,
      sampleRate,
    );

    // Part 1: Original audio before region (with fade-out at end)
    if (regionStartSample > 0) {
      const preSource = offlineCtx.createBufferSource();
      preSource.buffer = workBuffer;
      const preGain = offlineCtx.createGain();
      preGain.gain.setValueAtTime(1, 0);
      if (regionStartSample > crossfadeSamples) {
        preGain.gain.setValueAtTime(
          1,
          (regionStartSample - crossfadeSamples) / sampleRate,
        );
      }
      preGain.gain.linearRampToValueAtTime(0, regionStartSample / sampleRate);
      preSource.connect(preGain);
      preGain.connect(offlineCtx.destination);
      preSource.start(0, 0, regionStart);
    }

    // Part 2: Punch recording (fade-in at start, fade-out at end)
    const punchSource = offlineCtx.createBufferSource();
    punchSource.buffer = punchBuffer;
    const punchGain = offlineCtx.createGain();
    punchGain.gain.setValueAtTime(0, regionStart);
    punchGain.gain.linearRampToValueAtTime(
      1,
      regionStart + crossfadeSamples / sampleRate,
    );
    const punchEnd = regionStart + punchBuffer.duration;
    if (punchEnd > regionEnd - crossfadeSamples / sampleRate) {
      punchGain.gain.setValueAtTime(
        1,
        regionEnd - crossfadeSamples / sampleRate,
      );
      punchGain.gain.linearRampToValueAtTime(0, regionEnd);
    }
    punchSource.connect(punchGain);
    punchGain.connect(offlineCtx.destination);
    punchSource.start(regionStart);

    // Part 3: Original audio after region (with fade-in at start)
    if (regionEndSample < totalSamples) {
      const postSource = offlineCtx.createBufferSource();
      postSource.buffer = workBuffer;
      const postGain = offlineCtx.createGain();
      postGain.gain.setValueAtTime(0, regionEnd);
      postGain.gain.linearRampToValueAtTime(
        1,
        regionEnd + crossfadeSamples / sampleRate,
      );
      postSource.connect(postGain);
      postGain.connect(offlineCtx.destination);
      postSource.start(regionEnd, regionEnd);
    }

    const renderedBuffer = await offlineCtx.startRendering();
    return audioBufferToWav(renderedBuffer);
  } finally {
    audioCtx.close();
  }
};
